import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { useZoneGroupsByKeywordQuery, ZoneGroupsByKeywordQueryVariables } from "../../graphql"
import { ZoneGroupsList } from "./ZoneGroupsList"

export type ZoneGroupsByKeywordProps = {
	keyword: string
}

export const ZoneGroupsByKeyword: React.FC<ZoneGroupsByKeywordProps> = ({ keyword }) => {
	const [filter, setFilter] = useState({ keyword })

	const [pagination, setPagination] = useState<ZoneGroupsByKeywordQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useZoneGroupsByKeywordQuery({
		variables: { ...filter, pagination },
		pause: !fetch,
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword })
		}, 1000)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.zoneGroupsByKeyword.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.zoneGroupsByKeyword ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading zone groups</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.zoneGroupsByKeyword.zoneGroups && data.zoneGroupsByKeyword.zoneGroups.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.zoneGroupsByKeyword.zoneGroups.length || 0} zone group
						{(data.zoneGroupsByKeyword.zoneGroups.length || 0) > 1 ? "s" : ""}
					</Text>

					<ZoneGroupsList zoneGroups={data.zoneGroupsByKeyword.zoneGroups} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more zone groups</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any zone groups.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
